import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './CommunityForum.module.css';

const EMOJIS = ['👍', '❤️', '😂', '🎉', '😮', '😢'];

function CommunityForum() {
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const { token } = useSelector((state) => state.user);
	const [showModal, setShowModal] = useState(false);
	const [form, setForm] = useState({ title: '', content: '', image: null, imageUrl: '' });
	const [error, setError] = useState('');
	const [expandedComments, setExpandedComments] = useState({});
	const [commentForms, setCommentForms] = useState({});
	const [submitLoading, setSubmitLoading] = useState(false);

	// Fetch posts from API
	useEffect(() => {
		fetchPosts();
	}, [token]); // Add token as dependency since it's used in fetchPosts

	const fetchPosts = async () => {
		try {
			setLoading(true);
			const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/community/posts/`, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
			});
			
			if (response.ok) {
				const data = await response.json();
				setPosts(data.posts || data);
			} else {
				console.error('Failed to fetch posts');
			}
		} catch (error) {
			console.error('Error fetching posts:', error);
		} finally {
			setLoading(false);
		}
	};

	// Fetch comments for a specific post
	const fetchComments = async (postId) => {
		try {
			const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/community/posts/${postId}/comments/`, {
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
			});
			
			if (response.ok) {
				const data = await response.json();
				const comments = data.comments || data;
				// Update the specific post with fetched comments
				setPosts(prev => prev.map(post => 
					post.id === postId 
						? { ...post, comments }
						: post
				));
			}
		} catch (error) {
			console.error('Error fetching comments:', error);
		}
	};

	// Emoji handling
	const handleEmoji = async (postId, emoji, commentId = null) => {
		try {
			const endpoint = commentId			? `${process.env.REACT_APP_API_BASE_URL}/community/comments/${commentId}/emoji/`
			: `${process.env.REACT_APP_API_BASE_URL}/community/posts/${postId}/emoji/`;
			
			const response = await fetch(endpoint, {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					emoji: emoji,
					action: 'add'
				}),
			});

			if (response.ok) {
				// Update local state optimistically
				setPosts((prev) =>
					prev.map((post) => {
						if (post.id !== postId) return post;
						if (commentId) {
							return {
								...post,
								comments: post.comments.map((c) =>
									c.id === commentId
										? {
												...c,
												emoji_counts: {
													...c.emoji_counts,
													[emoji]: (c.emoji_counts?.[emoji] || 0) + 1,
												},
											}
										: c
								),
							};
						} else {
							return {
								...post,
								emoji_counts: {
									...post.emoji_counts,
									[emoji]: (post.emoji_counts?.[emoji] || 0) + 1,
								},
							};
						}
					})
				);
			}
		} catch (error) {
			console.error('Error handling emoji:', error);
		}
	};

	// Comment expand/collapse
	const toggleComments = async (postId) => {
		const isCurrentlyExpanded = expandedComments[postId];
		setExpandedComments((prev) => ({ ...prev, [postId]: !prev[postId] }));
		
		// Fetch comments when expanding for the first time
		if (!isCurrentlyExpanded) {
			await fetchComments(postId);
		}
	};

	// Add comment
	const handleCommentChange = (postId, e) => {
		setCommentForms((prev) => ({ ...prev, [postId]: e.target.value }));
	};
	
	const handleAddComment = async (postId) => {
		const content = commentForms[postId]?.trim();
		if (!content) return;
		
		try {
			const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/community/posts/${postId}/comments/create/`, {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ content }),
			});

			if (response.ok) {
				const newComment = await response.json();
				setPosts((prev) =>
					prev.map((post) =>
						post.id === postId
							? {
									...post,
									comments: [...(post.comments || []), newComment],
								}
							: post
					)
				);
				setCommentForms((prev) => ({ ...prev, [postId]: '' }));
			} else {
				console.error('Failed to add comment');
			}
		} catch (error) {
			console.error('Error adding comment:', error);
		}
	};

	// Post creation
	const handleOpenModal = () => {
		setForm({ title: '', content: '', image: null, imageUrl: '' });
		setError('');
		setShowModal(true);
	};
	const handleCloseModal = () => setShowModal(false);

	const handleChange = (e) => {
		if (e.target.name === 'image') {
			setForm({ ...form, [e.target.name]: e.target.files[0] });
		} else {
			setForm({ ...form, [e.target.name]: e.target.value });
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.title || !form.content) {
			setError('Title and content are required.');
			return;
		}

		// Validate that user provided either a file or URL, not both
		if (form.image && form.imageUrl.trim()) {
			setError('Please provide either a file upload OR an image URL, not both.');
			return;
		}

		setSubmitLoading(true);
		try {
			let imageData = null;
			
			// Handle local file upload if provided
			if (form.image) {
				const formData = new FormData();
				formData.append('image', form.image);
				
				const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/community/upload/`, {
					method: 'POST',
					headers: {
						'Authorization': `Bearer ${token}`,
					},
					body: formData,
				});
				
				if (uploadResponse.ok) {
					const uploadResult = await uploadResponse.json();
					imageData = uploadResult.image_url || uploadResult.image_id;
				} else {
					setError('Failed to upload image. Please try again.');
					setSubmitLoading(false);
					return;
				}
			}

			// Create post
			const postData = {
				title: form.title,
				content: form.content,
			};
			
			// Add image data - either from upload or external URL
			if (imageData) {
				// From upload
				postData.image = imageData;
			} else if (form.imageUrl.trim()) {
				// From external URL
				postData.image_url = form.imageUrl.trim();
			}

			const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/community/posts/create/`, {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${token}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(postData),
			});

			if (response.ok) {
				const newPost = await response.json();
				setPosts(prev => [newPost, ...prev]);
				setShowModal(false);
				setForm({ title: '', content: '', image: null, imageUrl: '' });
			} else {
				setError('Failed to create post. Please try again.');
			}
		} catch (error) {
			console.error('Error creating post:', error);
			setError('Failed to create post. Please try again.');
		} finally {
			setSubmitLoading(false);
		}
	};

	return (
		<div className={styles.page}>
			{loading ? (
				<div className={styles.loading}>
					<div className={styles.spinner}></div>
					<p>Loading community posts...</p>
				</div>
			) : (
				<div className={styles.timeline}>
					{posts.map((post) => (
						<div key={post.id} className={styles.forumCard}>
							<div className={styles.postHeader}>
								<span className={styles.postTitle}>{post.title}</span>
								<span className={styles.postAuthor}>by {post.author?.name || post.author}</span>
								<span className={styles.postDate}>
									{new Date(post.created_at || post.date).toLocaleDateString()}
								</span>
							</div>
							{(post.image_url || post.image) && (
								<img
									src={post.image_url || post.image}
									alt="Post visual"
									className={styles.postImage}
								/>
							)}
							<div className={styles.postContent}>{post.content}</div>
							<div className={styles.emojiBar}>
								{EMOJIS.map((emoji) => (
									<button
										key={emoji}
										className={styles.emojiBtn}
										onClick={() => handleEmoji(post.id, emoji)}
										type="button"
									>
										{emoji} {(post.emoji_counts?.[emoji] || post.emojis?.[emoji]) > 0 ? (post.emoji_counts?.[emoji] || post.emojis?.[emoji]) : ''}
									</button>
								))}
							</div>
							<div className={styles.commentsSection}>
								<button
									className={styles.toggleCommentsBtn}
									onClick={() => toggleComments(post.id)}
									type="button"
								>
									{expandedComments[post.id]
										? 'Hide Comments'
										: `View Comments (${post.comments?.length || post.comments_count || 0})`}
								</button>
								{!expandedComments[post.id] && (post.comments?.length > 0) && (
									<div className={styles.collapsedEmojis}>
										{post.comments.map((c) =>
											EMOJIS.map(
												(emoji) =>
													(c.emoji_counts?.[emoji] || c.emojis?.[emoji]) > 0 && (
														<span key={c.id + emoji} className={styles.emojiCount}>
															{emoji} {c.emoji_counts?.[emoji] || c.emojis?.[emoji]}
														</span>
													)
											)
										)}
									</div>
								)}
								{expandedComments[post.id] && (
									<div className={styles.commentsList}>
										{(post.comments || []).map((c) => (
											<div key={c.id} className={styles.commentCard}>
												<div className={styles.commentHeader}>
													<span className={styles.commentAuthor}>{c.author?.name || c.author}</span>
													<span className={styles.commentDate}>
														{new Date(c.created_at || c.date).toLocaleDateString()}
													</span>
												</div>
												<div className={styles.commentContent}>{c.content}</div>
												<div className={styles.emojiBar}>
													{EMOJIS.map((emoji) => (
														<button
															key={emoji}
															className={styles.emojiBtn}
															onClick={() => handleEmoji(post.id, emoji, c.id)}
															type="button"
														>
															{emoji} {(c.emoji_counts?.[emoji] || c.emojis?.[emoji]) > 0 ? (c.emoji_counts?.[emoji] || c.emojis?.[emoji]) : ''}
														</button>
													))}
												</div>
											</div>
										))}
										<div className={styles.addCommentBox}>
											<input
												className={styles.input}
												placeholder="Add a comment..."
												value={commentForms[post.id] || ''}
												onChange={(e) => handleCommentChange(post.id, e)}
												onKeyDown={(e) => {
													if (e.key === 'Enter') handleAddComment(post.id);
												}}
											/>
											<button
												className={styles.submitBtn}
												type="button"
												onClick={() => handleAddComment(post.id)}
											>
												Post
											</button>
										</div>
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			)}
			<button
				className={styles.fab}
				aria-label="Add Post"
				onClick={handleOpenModal}
			>
				+
			</button>
			{showModal && (
				<div className={styles.modalOverlay}>
					<div className={styles.modal}>
						<h3>Add New Post</h3>
						<form onSubmit={handleSubmit} className={styles.modalForm}>
							<input
								name="title"
								placeholder="Title"
								value={form.title}
								onChange={handleChange}
								className={styles.input}
							/>
							<textarea
								name="content"
								placeholder="Content"
								value={form.content}
								onChange={handleChange}
								className={styles.input}
								rows={4}
							/>
							<div className={styles.imageOptions}>
								<label className={styles.imageOptionLabel}>
									<strong>Image Options:</strong>
								</label>
								<input
									name="image"
									type="file"
									accept="image/*"
									onChange={handleChange}
									className={styles.input}
									placeholder="Upload from computer"
								/>
								<div className={styles.orDivider}>OR</div>
								<input
									name="imageUrl"
									type="url"
									placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
									value={form.imageUrl}
									onChange={handleChange}
									className={styles.input}
								/>
							</div>
							{error && <div className={styles.error}>{error}</div>}
							<div className={styles.modalActions}>
								<button
									type="button"
									onClick={handleCloseModal}
									className={styles.cancelBtn}
									disabled={submitLoading}
								>
									Cancel
								</button>
								<button 
									type="submit" 
									className={styles.submitBtn}
									disabled={submitLoading}
								>
									{submitLoading ? 'Posting...' : 'Post'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

export default CommunityForum;
